// Shared clip-share helper.
//
// Flow by platform:
//   Mobile (Web Share API + canShare({files})): native share sheet with
//     the .webm file attached — best UX for TikTok/DMs.
//   Desktop: opens a new BACKGROUND tab with the clip embedded inline
//     + a DOWNLOAD button. The tab fetches the blob URL into its OWN
//     Blob on page load so the video and download button keep working
//     even after the opener tab revokes the URL (e.g. the next death's
//     blob overwrites `lastBlobUrl`). No auto-download — user chooses
//     whether to save from inside the tab.
//   Blocked popup fallback: fire the download directly so the clip
//     isn't lost.

export type ShareClipInput = {
  blobUrl: string;
  filename: string;
  shareText: string;
  pageUrl?: string;
  // Optional UI callback for flash feedback: called with message + hex
  // color. The host button typically maps this to its own label.
  onStatus?: (msg: string, color: string) => void;
  // Optional toast hook — when the new tab opens we show a confirm
  // so the player notices (the tab is backgrounded so it's easy to
  // miss). Caller wires `hud.showToast` here.
  onToast?: (msg: string) => void;
};

type NavShare = Navigator & {
  canShare?: (d: { files?: File[] }) => boolean;
  share?: (d: { files?: File[]; text?: string; url?: string; title?: string }) => Promise<void>;
};

export async function shareClip(input: ShareClipInput): Promise<void> {
  const { blobUrl, filename, shareText, onStatus, onToast } = input;
  const pageUrl = input.pageUrl ?? (typeof location !== 'undefined' ? location.href : '');

  // Mobile-first: native share with attached file.
  try {
    const blob = await fetch(blobUrl).then((r) => r.blob());
    const file = new File([blob], filename, { type: blob.type });
    const nav = navigator as NavShare;
    if (nav.canShare?.({ files: [file] }) && nav.share) {
      await nav.share({ files: [file], text: shareText, url: pageUrl, title: 'Game clip' });
      onStatus?.('SHARED ✓', '#86efac');
      return;
    }
  } catch (err) {
    console.info('[share] native share unavailable', err);
  }

  // Desktop: open a new background tab ONLY. No auto-download — the
  // user decides whether to save via the in-tab DOWNLOAD button. The
  // tab clones the blob into its own context on load so it survives
  // the opener revoking its blob URL.
  const openedBackgroundTab = tryOpenBackgroundTab(blobUrl, filename);
  if (openedBackgroundTab) {
    onStatus?.('OPENED IN TAB', '#86efac');
    onToast?.('Clip opened in background tab');
    return;
  }

  // Popup blocked — save the clip directly so it isn't lost.
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  onStatus?.('SAVED (tab blocked)', '#fde047');
  onToast?.('Tab blocked — clip saved to Downloads');
}

/**
 * Open a new browser tab showing the clip inline with a DOWNLOAD
 * button. Returns true on success, false if the browser blocked the
 * open.
 *
 * Background-tab trick: we simulate a Ctrl/Cmd-click on a synthetic
 * `<a target="_blank">`. Most browsers honor the modifier key and
 * open the link in a BACKGROUND tab — same as if the user had
 * ctrl-clicked a link themselves. Plain `window.open()` almost
 * always opens foreground which is exactly what the user didn't
 * want.
 *
 * The anchor points at a `blob:` URL of an HTML shell that inlines
 * the clip + a DOWNLOAD button. Since the shell is a data: style
 * blob, we can hand the new tab everything it needs without a
 * window.open handle (which we wouldn't have access to anyway via
 * the Ctrl-click path — the anchor opens natively).
 */
function tryOpenBackgroundTab(blobUrl: string, filename: string): boolean {
  try {
    const shellHtml = buildShellHtml(blobUrl, filename);
    const shellBlob = new Blob([shellHtml], { type: 'text/html' });
    const shellUrl = URL.createObjectURL(shellBlob);

    const a = document.createElement('a');
    a.href = shellUrl;
    a.target = '_blank';
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);

    // Dispatch a synthetic click with the platform's "open in new
    // background tab" modifier held. Chrome/Firefox/Edge all honor
    // this. Safari is hit-or-miss but usually respects it too.
    const ev = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
      button: 0,
      ctrlKey: true,
      metaKey: true,
      shiftKey: false,
    });
    a.dispatchEvent(ev);
    a.remove();

    // Revoke the shell blob URL later — the tab needs a moment to
    // actually fetch it before we can free the reference.
    setTimeout(() => {
      try { URL.revokeObjectURL(shellUrl); } catch { /* ignore */ }
    }, 60_000);

    // Belt-and-suspenders: refocus the game window in case the new
    // tab stole it.
    try { window.focus(); } catch { /* ignore */ }
    return true;
  } catch (err) {
    console.info('[share] background tab open blocked', err);
    return false;
  }
}

function buildShellHtml(blobUrl: string, filename: string): string {
  const safeBlob = JSON.stringify(blobUrl);
  const safeName = JSON.stringify(filename);
  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Virgin vs Chad — Death Clip</title>
  <style>
    :root { color-scheme: dark; }
    html, body {
      background: #0b0303; color: #fecaca;
      font: 700 14px system-ui, -apple-system, sans-serif;
      margin: 0;
    }
    body {
      min-height: 100vh;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 18px; padding: 24px;
      box-sizing: border-box;
    }
    h1 {
      font: 900 28px Impact, "Arial Black", system-ui, sans-serif;
      letter-spacing: 6px; margin: 0; color: #fca5a5;
      text-shadow: 0 0 20px rgba(239,68,68,.45);
    }
    .hint {
      font: 700 10px ui-monospace, monospace;
      letter-spacing: 2.5px; opacity: .55; color: #fecaca;
    }
    video {
      width: min(960px, 94vw);
      aspect-ratio: 16 / 9;
      border: 2px solid #ef4444;
      border-radius: 14px;
      background: #000;
      box-shadow: 0 0 48px rgba(239,68,68,.35);
    }
    .bar { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
    button {
      padding: 10px 22px;
      background: rgba(15,23,42,.85);
      border: 2px solid #fde047;
      border-radius: 10px;
      color: #fde047;
      font: 900 12px system-ui, sans-serif;
      letter-spacing: 2.5px; cursor: pointer;
      transition: transform .1s ease-out;
    }
    button:hover { transform: translateY(-1px); }
    .name { font: 700 11px ui-monospace, monospace; opacity: .7; }
  </style>
</head>
<body>
  <h1>DEATH CLIP</h1>
  <div class="hint">LOCAL PREVIEW · WILL BE SERVER-HOSTED LATER</div>
  <video id="v" controls autoplay loop muted playsinline></video>
  <div class="name">${escapeHtml(filename)}</div>
  <div class="bar">
    <button id="dl" disabled>📎 DOWNLOAD</button>
  </div>
  <div id="status" class="hint" style="opacity:.75;">LOADING CLIP…</div>
  <script>
    // Fetch the opener's blob URL into a LOCAL Blob so the tab
    // survives the opener revoking its URL (which happens every time
    // the player dies again in the game tab). Once fetched, we
    // create a brand-new blob: URL owned by this tab and use that
    // for both the video src and the download anchor.
    (function () {
      var v = document.getElementById('v');
      var dl = document.getElementById('dl');
      var status = document.getElementById('status');
      var src = ${safeBlob};
      var localUrl = null;
      fetch(src).then(function (r) { return r.blob(); }).then(function (b) {
        localUrl = URL.createObjectURL(b);
        v.src = localUrl;
        dl.disabled = false;
        status.textContent = 'CLIP READY · ' + (b.size / 1024).toFixed(1) + ' KB';
      }).catch(function (err) {
        status.textContent = 'FETCH FAILED — ' + err;
      });
      dl.onclick = function () {
        if (!localUrl) return;
        var a = document.createElement('a');
        a.href = localUrl;
        a.download = ${safeName};
        document.body.appendChild(a);
        a.click();
        a.remove();
      };
    })();
  </script>
</body>
</html>`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] as string));
}
